/**
 * dashboard_prefs.repository.ts — 홈 대시보드 위젯 레이아웃을 사용자별로 Firestore 보관.
 *
 *   문서: users/{uid}/preferences/dashboard_{보드} = { layout: Widget[], updated_at }
 *     보드 = 사업 단위 홈(domain/space.ts `homeBoardKey`, 예: joongang_rmc · gyeongwon_ascon).
 *     중앙·경원 홈이 배치·위젯 설정을 따로 갖는다(2026-10-23).
 *   옛 문서 users/{uid}/preferences/dashboard(전 사업 공용 한 벌)는 지우지 않고 남겨 둔다 —
 *     보드 문서가 아직 없을 때 첫 배치를 복사해 오는 출처(loadLegacy). 이 화면은 더는 쓰지 않는다.
 *   firestore.rules 의 `match /users/{uid}/preferences/{key}` 가 본인 read/write 를
 *   이미 허용하므로 rules 변경·배포 불필요 (salesPrefs 와 동일 경로 체계).
 *
 *   salesPrefsRepo 구조를 복제. 로그아웃 상태면 watch 는 즉시 null, save 는 no-op.
 *
 *   레이아웃 위젯 타입은 Dashboard.tsx 내부 `Widget` 와 동일 shape 이지만,
 *   순환 import 를 피하려고 여기서 구조 동등한 타입을 별도 정의한다.
 */
import { doc, getDoc, onSnapshot, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

/** Dashboard.tsx `Widget` 와 구조 동등 (직렬화 대상). 필드 추가 시 양쪽 동기화. */
export interface DashboardWidget {
  id: string;
  type: "kpi" | "bar" | "bar-combo" | "line" | "table" | "today" | "today2" | "calendar";
  x: number;
  y: number;
  w: number;
  h: number;
  kpiIndex?: number;
  compactOrder?: number;
  compactSize?: "sm" | "tall" | "wide" | "full";
  /** 위젯별 설정 — WidgetSettings (flat object). 직렬화에 그대로 포함됨 */
  settings?: Record<string, unknown>;
}

export interface DashboardPrefs {
  layout: DashboardWidget[];
  /** 마지막으로 받아들인 부서 기본 배치의 apply_version(2026-11-02). 없으면 0 */
  roleLayoutApplied: number;
}

/** 옛 공용 문서 ID — 보드별 문서가 없을 때 복사 출처 */
const LEGACY_DOC_ID = "dashboard";

function boardDocId(board: string): string {
  return `${LEGACY_DOC_ID}_${board}`;
}

function layoutOf(d: Record<string, unknown> | undefined): DashboardWidget[] | null {
  return d && Array.isArray(d.layout) ? (d.layout as DashboardWidget[]) : null;
}
function prefsOf(d: Record<string, unknown> | undefined): DashboardPrefs | null {
  const layout = layoutOf(d);
  if (!layout) return null;
  const applied = d && typeof d.role_layout_applied === "number" ? d.role_layout_applied : 0;
  return { layout, roleLayoutApplied: applied };
}

export const dashboardPrefsRepo = {
  /**
   * 본인 대시보드 레이아웃 구독 (로그인 필요).
   * - 미로그인 → 즉시 null + no-op unsub.
   * - 문서 없음 → null (호출측이 옛 공용 문서·localStorage 시드 fallback 사용).
   * - layout 필드가 배열이 아니면 null 취급.
   */
  watch(board: string, cb: (prefs: DashboardPrefs | null) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      cb(null);
      return () => {};
    }
    return onSnapshot(
      doc(db, "users", uid, "preferences", boardDocId(board)),
      (snap) => cb(prefsOf(snap.data())),
      () => cb(null),
    );
  },

  /** 옛 공용 문서(전 사업 한 벌)의 레이아웃 — 보드 문서가 처음 만들어질 때 복사본으로 쓴다. 없거나 실패 → null */
  async loadLegacy(): Promise<DashboardWidget[] | null> {
    const uid = auth.currentUser?.uid;
    if (!uid) return null;
    try {
      const snap = await getDoc(doc(db, "users", uid, "preferences", LEGACY_DOC_ID));
      return layoutOf(snap.data());
    } catch {
      return null;
    }
  },

  /** 레이아웃 저장 (merge). 미로그인 시 조용히 무시 (오프라인/로그아웃 보호).
   *  ⚠️ Firestore 는 undefined 필드값에 setDoc 이 throw 됨(비-KPI 위젯의 kpiIndex:undefined 등).
   *  JSON 라운드트립으로 undefined 키를 제거해 저장 실패를 막는다. */
  async save(board: string, layout: DashboardWidget[], roleLayoutApplied?: number): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    const clean = JSON.parse(JSON.stringify(layout)) as DashboardWidget[];
    await setDoc(
      doc(db, "users", uid, "preferences", boardDocId(board)),
      {
        layout: clean,
        ...(roleLayoutApplied !== undefined ? { role_layout_applied: roleLayoutApplied } : {}),
        updated_at: serverTimestamp(),
      },
      { merge: true },
    );
  },
};
